import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePdfIcon } from '@phosphor-icons/react';
import { Button, Dialog, EmptyState } from '@origo/origo-ui';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import DocumentList, { type DocumentAction } from './components/DocumentList';
import type { DocumentItem } from '../../types';
import styles from './DocumentsPage.module.css';

type StubKind = 'upload' | DocumentAction | null;

interface DocumentsPageProps {
    items: DocumentItem[];
    onOpen: (id: string) => void;
}

const stubKeyMap: Record<Exclude<StubKind, null>, 'uploadStub' | 'replaceStub' | 'deleteStub'> = {
    upload: 'uploadStub',
    replace: 'replaceStub',
    delete: 'deleteStub',
};

const DocumentsPage: React.FC<DocumentsPageProps> = ({ items, onOpen }) => {
    const { t } = useTranslation();
    const [stub, setStub] = useState<StubKind>(null);

    const isEmpty = items.length === 0;

    const stubKey = stub ? stubKeyMap[stub] : null;
    const stubCopy = stubKey
        ? {
              title: t(`documents.${stubKey}.title`),
              body: t(`documents.${stubKey}.body`),
              close: t(`documents.${stubKey}.close`),
          }
        : null;

    return (
        <article className={styles.page}>
            <PlanDetailHeader
                title={t('documents.title')}
                subtitle={t('documents.subtitle')}
                actions={
                    !isEmpty ? (
                        <Button variant="secondary" size="md" onClick={() => setStub('upload')}>
                            {t('documents.newDocument')}
                        </Button>
                    ) : undefined
                }
            />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<FilePdfIcon size={32} weight="duotone" />}
                        title={t('documents.empty.title')}
                        description={t('documents.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={() => setStub('upload')}>
                                {t('documents.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    <DocumentList
                        items={items}
                        onOpen={onOpen}
                        onAction={action => setStub(action)}
                    />
                )}
            </div>
            <Dialog
                open={stub !== null}
                onClose={() => setStub(null)}
                title={stubCopy?.title ?? ''}
                footer={
                    <Button variant="primary" onClick={() => setStub(null)}>
                        {stubCopy?.close ?? ''}
                    </Button>
                }
            >
                <p>{stubCopy?.body ?? ''}</p>
            </Dialog>
        </article>
    );
};

export default DocumentsPage;
